/**
 * Home Building Hub episode manifest & lookup helpers.
 *
 * Content note: All episodes originate from Home Building Hub
 * (https://homebuildinghub.com.au) and are embedded on BuildPilot under a
 * content partnership. Every surface that renders an episode MUST show the
 * "Sponsored by Home Building Hub" attribution (see PartnerAttribution.jsx).
 *
 * Manifest is regenerated by /app/backend/scripts/scrape_hbh_episodes.py.
 * Do not hand-edit hbhEpisodes.json.
 */
import manifest from './hbhEpisodes.json';

export const HBH_PARTNER = {
  name: 'Home Building Hub',
  shortName: 'HBH',
  homeUrl: 'https://homebuildinghub.com.au',
  episodesUrl: 'https://homebuildinghub.com.au/episodes/',
  logo: '/images/partners/home-building-hub-logo.png',
  tagline: 'Australia\'s independent podcast on new home building.',
};

export const HBH_EPISODES = manifest.episodes;
export const HBH_EPISODE_COUNT = manifest.count;

/**
 * State-focus map: episodes that are Victoria-specific (regulatory content,
 * Melbourne-only market updates, VBA / Homebuyer Fund / VIC building
 * legislation etc.). Everything not in this set is treated as AU-generic
 * and shown to viewers in every state.
 *
 * Kept in this file (not in the JSON manifest) because the manifest is
 * regenerated by the scraper and would clobber hand-editing. Curator-
 * maintained via keyword audit on scraper output.
 * Last audited: 2026-03-05.
 */
export const HBH_VIC_ONLY_SLUGS = new Set([
  'ep-134-explained-new-buyer-protection-laws-from-1st-july-2026',
  'ep-132-why-you-should-visit-the-2026-melbourne-home-show-this-weekend',
  'ep-126-no-gas-from-2027-which-states-are-impacted',
  'ep-125-6-key-steps-to-get-ready-for-building-in-2026',
  'ep-107-exclusive-hia-victoria-boss-keith-ryan-breaks-down-the-new-building-watchdog',
  'ep-84-hia-explained-what-it-does-why-it-matters-with-executive-director-keith-ryan',
  'ep-44-new-regulations-explained-in-16-minutes',
  'ep-38-victorian-2024-all-electric-requirements-explained',
  'ep-35-how-to-determine-which-house-design-will-fit-on-your-block',
  'ep-3-with-porter-davis-down-should-you-be-building-right-now',
]);

export const getEpisodeStateFocus = (episode) =>
  HBH_VIC_ONLY_SLUGS.has(episode?.slug) ? 'VIC' : 'AU';

/**
 * Filter a list of episodes to those visible to viewers in `state`.
 *   - state = 'SA' → hides VIC-only episodes (they would confuse SA viewers
 *     since the regulatory content does not apply in SA).
 *   - state = 'VIC' → shows everything (VIC episodes + AU-generic).
 *   - state = null (no user state / crawler) → shows everything (AU-generic
 *     PLUS VIC episodes are still fine on any SA URL — Google crawls both
 *     states of the site independently).
 */
export const filterHbhEpisodesForState = (episodes, state) => {
  if (state !== 'SA') return episodes;
  return episodes.filter((e) => getEpisodeStateFocus(e) !== 'VIC');
};

// Public topic taxonomy shown in filters. Order matters (drives UI).
export const HBH_TOPICS = [
  { slug: 'packages', label: 'House & Land' },
  { slug: 'builders', label: 'Choosing a Builder' },
  { slug: 'land', label: 'Buying Land' },
  { slug: 'contracts', label: 'Contracts' },
  { slug: 'finance', label: 'Finance' },
  { slug: 'first-home', label: 'First Home Buyer' },
  { slug: 'design', label: 'Design & Inclusions' },
  { slug: 'regulations', label: 'Regulations' },
  { slug: 'site-costs', label: 'Site Costs' },
  { slug: 'process', label: 'The Process' },
  { slug: 'market', label: 'Market Updates' },
  { slug: 'investor', label: 'Investors' },
];

const _bySlug = Object.fromEntries(HBH_EPISODES.map((e) => [e.slug, e]));
const _byNumber = Object.fromEntries(HBH_EPISODES.map((e) => [e.number, e]));

export const getHbhEpisodeBySlug = (slug) => _bySlug[slug] || null;
export const getHbhEpisodeByNumber = (n) => _byNumber[Number(n)] || null;

/**
 * Return up to `limit` episodes matching ANY of the given topics.
 * Falls back to most-recent when no topic hits.
 */
export const getHbhEpisodesByTopics = (topics, limit = 3, excludeSlug = null) => {
  const wanted = new Set((topics || []).filter(Boolean));
  const hits = HBH_EPISODES.filter(
    (e) => e.slug !== excludeSlug && (e.topics || []).some((t) => wanted.has(t))
  );
  return hits.slice(0, limit);
};

export const getHbhEpisodesByTopic = (topic, limit = null) => {
  const list = HBH_EPISODES.filter((e) => (e.topics || []).includes(topic));
  return limit ? list.slice(0, limit) : list;
};

export const getRecentHbhEpisodes = (limit = 12) => HBH_EPISODES.slice(0, limit);

/**
 * YouTube nocookie embed URL (privacy-friendly, no tracking cookie set until
 * a viewer clicks play). Passing origin protects from clickjacking.
 */
export const hbhEmbedUrl = (episode) =>
  episode?.youtubeId
    ? `https://www.youtube-nocookie.com/embed/${episode.youtubeId}?rel=0&modestbranding=1`
    : null;
